/**
 * Façade LÉGÈRE de télémétrie.
 *
 * Ne dépend QUE des packages `@opentelemetry/api` et `@opentelemetry/api-logs`
 * (quelques Ko) : elle peut donc être importée partout dans l'app (interceptors,
 * ErrorHandler, services) SANS tirer le SDK OpenTelemetry complet dans le bundle
 * initial. Le SDK lourd (exporters, instrumentations) vit dans `telemetry.ts`,
 * chargé dynamiquement par `main.ts`.
 *
 * Tant que `telemetry.ts` n'a pas enregistré les providers globaux, `getLogger`
 * / `getTracer` renvoient des implémentations no-op : tous les appels ci-dessous
 * sont donc sûrs même avant l'init (et inertes si la télémétrie est désactivée).
 */
import { trace, type Attributes, type Tracer } from '@opentelemetry/api';
import { logs, SeverityNumber } from '@opentelemetry/api-logs';

/** Nom du scope d'instrumentation (instrumentation scope) commun. */
export const TELEMETRY_SCOPE = 'zazu-web';

let enabled = false;

/**
 * Attributs utilisateur courants (id + rôles), injectés dans chaque log et,
 * via le SpanProcessor de `telemetry.ts`, dans chaque span.
 * Jamais d'email ni de nom : modèle BFF, pas de PII côté télémétrie.
 */
let userAttributes: Attributes = {};

export function markTelemetryEnabled(value: boolean): void {
  enabled = value;
}

export function isTelemetryEnabled(): boolean {
  return enabled;
}

export interface TelemetryUser {
  id: string;
  roles: string[];
}

export function setTelemetryUser(user: TelemetryUser | null): void {
  userAttributes = user
    ? { 'user.id': user.id, 'user.roles': user.roles.join(',') }
    : {};
}

export function getUserAttributes(): Attributes {
  return userAttributes;
}

export function getTracer(): Tracer {
  return trace.getTracer(TELEMETRY_SCOPE);
}

/** Émet un log structuré (→ Loki). */
export function recordLog(
  severity: SeverityNumber,
  message: string,
  attributes: Attributes = {},
): void {
  if (!enabled) return;
  logs.getLogger(TELEMETRY_SCOPE).emit({
    severityNumber: severity,
    body: message,
    attributes: { ...userAttributes, ...attributes },
  });
}

/** Émet une erreur sous forme de log avec les conventions `exception.*` (→ Loki). */
export function recordError(error: unknown, attributes: Attributes = {}): void {
  if (!enabled) return;
  const err = error instanceof Error ? error : new Error(String(error));
  logs.getLogger(TELEMETRY_SCOPE).emit({
    severityNumber: SeverityNumber.ERROR,
    body: err.message,
    attributes: {
      ...userAttributes,
      'exception.type': err.name,
      'exception.message': err.message,
      'exception.stacktrace': err.stack ?? '',
      ...attributes,
    },
  });
}

/** Émet un évènement applicatif nommé (→ Loki). */
export function recordEvent(name: string, attributes: Attributes = {}): void {
  if (!enabled) return;
  logs.getLogger(TELEMETRY_SCOPE).emit({
    severityNumber: SeverityNumber.INFO,
    body: name,
    attributes: { 'event.name': name, ...userAttributes, ...attributes },
  });
}

export { SeverityNumber };
